/**
 * Routing.
 *
 * Every navigation entry gets a real destination, generated from the same
 * navigation model the sidebar reads, so the two cannot drift apart.
 *
 * One route sits OUTSIDE the shell, because it has nothing to navigate to:
 * `/signin` (Sign in with GFT, and the Unassigned and deactivated states).
 *
 * Note what the guard below is and is not. It sends a browser that cannot use
 * Atlas to the auth screen so the user sees something sensible -- that is
 * presentation. It is **not** access control: every request the shell issues is
 * authorized server-side, and deep-linking past this guard gains nothing but a
 * 401 or a 403. The client is never the security boundary.
 *
 * f9.2: `/` lands on the first entry the server permitted, or on the landing
 * state when it permitted none, and a route whose key the server did not send
 * renders `Not available to you` in place -- see `RequirePermittedNavigation`.
 */

import type { ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { SIGN_IN_PATH, signInScreen } from '@/auth/signIn';
import { NAV_ENTRIES, type NavEntry } from '@/navigation/navigation';
import { AuditLogPage } from '@/routes/audit-log/AuditLogPage';
import { BudgetPage } from '@/routes/budget/BudgetPage';
import { BusinessUnitGroupDetailPage } from '@/routes/business-unit-groups/BusinessUnitGroupDetail';
import { BusinessUnitGroupsPage } from '@/routes/business-unit-groups/BusinessUnitGroupsPage';
import { OrganizationOverviewPage } from '@/routes/organization-overview/OrganizationOverviewPage';
import { BusinessUnitDetailPage } from '@/routes/business-units/BusinessUnitDetail';
import { BusinessUnitsPage } from '@/routes/business-units/BusinessUnitsPage';
import { ClientDetailPage } from '@/routes/clients/ClientDetail';
import { ClientsPage } from '@/routes/clients/ClientsPage';
import { ConfigurationPage } from '@/routes/configuration/ConfigurationPage';
import { CycleOverview } from '@/routes/cycles/CycleOverview';
import { CyclesPage } from '@/routes/cycles/CyclesPage';
import { CycleStepPage } from '@/routes/cycles/CycleStepPage';
import { CycleWorkspaceLayout } from '@/routes/cycles/CycleWorkspaceLayout';
import { DashboardPage } from '@/routes/dashboard/DashboardPage';
import { DataImportPage } from '@/routes/data-import/DataImportPage';
import { StagedBatchPage } from '@/routes/data-import/StagedBatchPage';
import { NotFound } from '@/routes/NotFound';
import { PhasePlaceholder } from '@/routes/PhasePlaceholder';
import { ReferenceDataPage } from '@/routes/reference-data/ReferenceDataPage';
import { SignIn } from '@/routes/SignIn';
import { UsersPage } from '@/routes/users/UsersPage';
import { AppShell } from '@/shell/AppShell';
import { BootScreen } from '@/shell/BootScreen';
import { ForecastEntryRedirect } from '@/features/forecast/ForecastEntryRedirect';
import { ReviewPage } from '@/routes/cycles/review/ReviewPage';
import { ArtifactDetailPage } from '@/routes/artifacts/ArtifactDetailPage';
import { ArtifactsPage } from '@/routes/artifacts/ArtifactsPage';
import { ReconciliationQueuePage } from '@/routes/cycles/reconciliation/ReconciliationQueuePage';
import { LandingRoute, RequirePermittedNavigation } from '@/shell/RequirePermittedNavigation';
import { useSessionScope } from '@/session/SessionScopeContext';

/**
 * Navigation keys whose feature has shipped, mapped to their screen.
 *
 * A key absent here still resolves to the phase placeholder, so the routing
 * table and the sidebar keep reading the one navigation model. Each feature
 * that delivers a screen adds exactly one line.
 */
const SCREENS: Readonly<Record<string, ComponentType>> = {
  artifacts: ArtifactsPage, // f15.2
  'audit-log': AuditLogPage, // f1.5
  budget: BudgetPage, // f10.10 -- Finance and Admin only, both sides
  'business-unit-groups': BusinessUnitGroupsPage, // f4.1
  'business-units': BusinessUnitsPage, // f5.1
  clients: ClientsPage, // f6.1
  'organization-overview': OrganizationOverviewPage, // the four-layout hierarchy view
  configuration: ConfigurationPage, // f2.1
  cycles: CyclesPage, // f10.2
  'data-import': DataImportPage, // f8.2 -- Platform Admin only, both sides
  dashboard: DashboardPage, // f16.1 -- the landing; every figure scoped server-side
  'reference-data': ReferenceDataPage, // f2.2
  users: UsersPage, // f3.1
};

/**
 * Screens reached from another screen rather than from the sidebar.
 *
 * They are NOT navigation entries -- nothing in the sidebar points at one, and
 * the server grants no key for them -- so they are declared here rather than in
 * the navigation model, which stays the single source of the menu.
 *
 * Each is still inside `RequireActiveAccount`, and each still authorizes
 * server-side on every call it makes.
 *
 * f9.2 guards each by its PARENT navigation key, for presentation only. An
 * id's membership of the caller's scope is not something the browser holds,
 * so the real protection stays the `404` the API answers for it.
 */
const DETAIL_ROUTES: readonly { path: string; navKey: string; element: ComponentType }[] = [
  // f4.1. An id outside the caller's visibility answers 404, so deep-linking
  // past the list gains nothing.
  { path: '/business-unit-groups/:groupId', navKey: 'business-unit-groups', element: BusinessUnitGroupDetailPage },
  // f5.1. Same rule: an id outside the caller's visibility answers 404.
  { path: '/business-units/:unitId', navKey: 'business-units', element: BusinessUnitDetailPage },
  // f6.1. Same rule again -- and this one is a TAB HOST: f7.1, f11.4 and
  // f15.1 each add a tab inside it rather than a route beside it. Engagements
  // deliberately have no navigation entry of their own (ADR-17).
  { path: '/clients/:clientId', navKey: 'clients', element: ClientDetailPage },
  // f8.2. The staged batch, addressed by UUID: `batch_number` is the label the
  // screen renders as `#0147` and is deliberately not routable, so a request
  // for `/data-import/147` is a 404 rather than somebody else's batch.
  { path: '/data-import/:batchId', navKey: 'data-import', element: StagedBatchPage },
];

function screenFor(entry: NavEntry) {
  const Screen = SCREENS[entry.key];
  return Screen ? <Screen /> : <PhasePlaceholder entry={entry} />;
}

function RequireActiveAccount() {
  const { accessState, isLoading, error } = useSessionScope();
  const location = useLocation();

  // The boot screen rather than a guess while the server is still answering.
  if (isLoading) return <BootScreen />;
  if (error) return <BootScreen error />;

  // `active` is the only state that gets the shell, and it is the SERVER that
  // decides who is in it. Holding a valid token is no longer enough: an
  // `unassigned` caller would otherwise reach a chrome with an empty sidebar
  // and a page that 403s, and a `deactivated` one would reach the same thing
  // with nothing explaining why.
  if (accessState !== 'active') {
    // Carry the destination, so the user lands where they meant to after
    // Sign in with GFT.
    return <Navigate to={signInScreen(location.pathname + location.search)} replace />;
  }

  return <AppShell />;
}

/**
 * Keeps an active user off the auth screen.
 *
 * Without this, someone already signed in who followed a stale bookmark would
 * be offered a sign-in button they have no use for.
 */
function AuthScreen() {
  const { accessState, isLoading } = useSessionScope();

  if (isLoading) return <BootScreen />;
  if (accessState === 'active') return <Navigate to="/" replace />;

  return <SignIn />;
}

export function App() {
  return (
    <Routes>
      <Route path={SIGN_IN_PATH} element={<AuthScreen />} />

      <Route element={<RequireActiveAccount />}>
        <Route index element={<LandingRoute />} />

        {NAV_ENTRIES.map((entry) => (
          <Route
            key={entry.key}
            path={entry.path}
            element={
              <RequirePermittedNavigation navKey={entry.key}>
                {screenFor(entry)}
              </RequirePermittedNavigation>
            }
          />
        ))}

        {DETAIL_ROUTES.map(({ path, navKey, element: Screen }) => (
          <Route
            key={path}
            path={path}
            element={
              <RequirePermittedNavigation navKey={navKey}>
                <Screen />
              </RequirePermittedNavigation>
            }
          />
        ))}

        {/* f11.1: forecast input for one cycle and Business Unit. Not behind
            the Finance-only `cycles` entry: managers edit their own lines, and
            the API scopes every row and refuses every out-of-scope write. */}
        {/* f11.3: without `?bu=` it goes to the caller's first unit (replace). */}
        <Route path="/cycles/:cycleId/forecast" element={<ForecastEntryRedirect />} />
        {/* f12.1: the review screen (the FS sheet). Reached from the cycle
            workspace, not a navigation entry; the API scopes every row. */}
        <Route path="/cycles/:cycleId/review" element={<ReviewPage />} />
        {/* f15.1: one artifact and its version history. Reached from an
            Artifacts tab; the API answers 404 outside the owning node's scope. */}
        <Route path="/artifacts/:artifactId" element={<ArtifactDetailPage />} />
        {/* f14.2: the reconciliation queue. Linked from the Finance/Admin cycle
            workspace; the API answers 403 to anyone else. */}
        <Route path="/cycles/:cycleId/reconciliation" element={<ReconciliationQueuePage />} />

        {/* f10.3: the cycle workspace -- a layout with the persistent step
            rail, and two children: the overview and one open step. Guarded by
            the `cycles` entry for presentation only; the workflow API answers
            403 to anyone who is not Finance or Admin. f11.3: a caller without
            workflow access gets the scoped variant with the Forecast input card. */}
        <Route
          path="/cycles/:cycleId"
          element={
            <RequirePermittedNavigation navKey="cycles">
              <CycleWorkspaceLayout />
            </RequirePermittedNavigation>
          }
        >
          <Route index element={<CycleOverview />} />
          <Route path="steps/:stepCode" element={<CycleStepPage />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
